import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Flex,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Tag,
  Avatar,
  Icon,
  useToast,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Tooltip,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  ButtonGroup
} from '@chakra-ui/react';
import {
  SearchIcon,
  DownloadIcon,
  RepeatIcon,
  ViewIcon,
  CheckCircleIcon,
  WarningIcon,
  InfoIcon,
  TimeIcon
} from '@chakra-ui/icons';
import {
  FiUsers,
  FiClock,
  FiAward,
  FiCalendar,
  FiActivity,
  FiCheckCircle,
  FiAlertCircle
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import axiosInstance from '../services/axiosInstance';
import ResponsiveLayout from '../shared/ResponsiveLayout';
import CobraSpinner from '../shared/CobraSpinner';

const fadeIn = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

export default function InstructorSeancesReport() {
  const toast = useToast();

  // Color tokens
  const secondaryColor = '#2C3E50';
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const hoverRowBg = useColorModeValue('gray.50', 'gray.700');

  // Date filters state
  const [preset, setPreset] = useState('thisMonth');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Data state
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    summary: {},
    instructors: [],
    period: {}
  });

  // Table filtering & search
  const [searchQuery, setSearchQuery] = useState('');
  const [seanceFilter, setSeanceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('seances');

  // Modal drill-down state for selected instructor
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedInstructor, setSelectedInstructor] = useState(null);

  // Helper to calculate date presets
  const applyPreset = useCallback((type) => {
    setPreset(type);
    const now = new Date();
    const formatDate = (d) => d.toISOString().split('T')[0];

    if (type === 'today') {
      const todayStr = formatDate(now);
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else if (type === 'thisWeek') {
      const day = now.getDay();
      const diffToMonday = now.getDate() - day + (day === 0 ? -6 : 1);
      const startOfWeek = new Date(now.setDate(diffToMonday));
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      setStartDate(formatDate(startOfWeek));
      setEndDate(formatDate(endOfWeek));
    } else if (type === 'thisMonth') {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setStartDate(formatDate(startOfMonth));
      setEndDate(formatDate(endOfMonth));
    } else if (type === 'lastMonth') {
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      setStartDate(formatDate(startOfLastMonth));
      setEndDate(formatDate(endOfLastMonth));
    } else if (type === 'allTime') {
      setStartDate('');
      setEndDate('');
    }
  }, []);

  // Set initial preset on mount
  useEffect(() => {
    applyPreset('thisMonth');
  }, [applyPreset]);

  // Fetch report data from API
  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await axiosInstance.get('/admin/instructor-seances', { params });
      if (response.data.success) {
        setReportData(response.data);
      }
    } catch (error) {
      console.error('Error fetching instructor seances report:', error);
      toast({
        title: 'خطأ في جلب بيانات الحصص',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top'
      });
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, toast]);

  useEffect(() => {
    if (preset !== 'custom' || (startDate && endDate)) {
      fetchReport();
    }
  }, [preset, startDate, endDate, fetchReport]);

  // Filtered & Sorted instructors
  const filteredInstructors = useMemo(() => {
    let list = reportData.instructors || [];

    // Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((inst) => {
        const username = (inst.instructor?.username || '').toLowerCase();
        const email = (inst.instructor?.email || '').toLowerCase();
        const phone = (inst.instructor?.phoneNumber || '').toLowerCase();
        const matieres = (inst.instructor?.matiers || []).join(' ').toLowerCase();
        return username.includes(q) || email.includes(q) || phone.includes(q) || matieres.includes(q);
      });
    }

    // Séance filter
    if (seanceFilter === 'withSeances') {
      list = list.filter((inst) => inst.seanceCount > 0);
    } else if (seanceFilter === 'noSeances') {
      list = list.filter((inst) => inst.seanceCount === 0);
    }

    // Sorting
    list = [...list].sort((a, b) => {
      if (sortBy === 'seances') {
        return b.seanceCount - a.seanceCount || b.totalMeetings - a.totalMeetings;
      }
      if (sortBy === 'meetings') {
        return b.totalMeetings - a.totalMeetings || b.seanceCount - a.seanceCount;
      }
      if (sortBy === 'duration') {
        return b.totalDurationSeconds - a.totalDurationSeconds;
      }
      if (sortBy === 'name') {
        return (a.instructor?.username || '').localeCompare(b.instructor?.username || '');
      }
      return 0;
    });

    return list;
  }, [reportData.instructors, searchQuery, seanceFilter, sortBy]);

  // Open instructor drill-down modal
  const handleOpenDetails = (instructorRecord) => {
    setSelectedInstructor(instructorRecord);
    onOpen();
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (!reportData.instructors || reportData.instructors.length === 0) {
      toast({
        title: 'لا توجد بيانات للتصدير',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    const excelData = reportData.instructors.map((inst, index) => ({
      '#': index + 1,
      'اسم الأستاذ': inst.instructor?.username || 'غير معروف',
      'البريد الإلكتروني': inst.instructor?.email || '',
      'رقم الهاتف': inst.instructor?.phoneNumber || '',
      'المواد الدراسية': (inst.instructor?.matiers || []).join(', '),
      'عدد الحصص المعتمدة (≥ 1س 20د)': inst.seanceCount || 0,
      'اللقاءات القصيرة (< 1س 20د)': inst.shortMeetingCount || 0,
      'إجمالي اللقاءات': inst.totalMeetings || 0,
      'إجمالي ساعات التدريس': inst.totalDurationFormatted || '0د',
      'إجمالي مدة الحصص المعتمدة': inst.seanceDurationFormatted || '0د',
      'معدل مدة اللقاء': inst.averageDurationFormatted || '0د',
      'الفترة من': startDate || 'البداية',
      'الفترة إلى': endDate || 'الآن'
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'تقرير حصص الأساتذة');

    const fileName = `تقرير_حصص_الأساتذة_${startDate || 'الكل'}_إلى_${endDate || 'الكل'}.xlsx`;
    XLSX.writeFile(wb, fileName);

    toast({
      title: 'تم تصدير التقرير بنجاح',
      description: `تم حفظ الملف: ${fileName}`,
      status: 'success',
      duration: 4000
    });
  };

  const summary = reportData.summary || {};

  return (
    <ResponsiveLayout>
      <Box dir="rtl" pb={12} pt={2}>
        <VStack spacing={8} align="stretch">

          {/* Page Header */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn}>
            <Flex
              justifyContent="space-between"
              alignItems={{ base: 'flex-start', md: 'center' }}
              flexDirection={{ base: 'column', md: 'row' }}
              bg={cardBg}
              p={6}
              borderRadius="2xl"
              boxShadow="sm"
              border="1px solid"
              borderColor={borderColor}
              gap={4}
            >
              <Box>
                <HStack spacing={3} mb={1}>
                  <Box p={3} bg="blue.500" color="white" borderRadius="xl" boxShadow="md">
                    <Icon as={FiAward} boxSize={6} />
                  </Box>
                  <Heading as="h1" size="lg" color={secondaryColor} fontWeight="800">
                    تقرير حصص الأساتذة المعتمدة
                  </Heading>
                </HStack>
                <Text color="gray.500" fontSize="sm" mt={1} mr={12}>
                  حساب وإحصاء عدد الحصص الرسمية لكل أستاذ (كل لقاء مباشر مدته لا تقل عن <Badge colorScheme="green" fontSize="xs">1 ساعة و 20 دقيقة</Badge>)
                </Text>
              </Box>

              <HStack spacing={3}>
                <Button
                  leftIcon={<RepeatIcon />}
                  onClick={fetchReport}
                  isLoading={loading}
                  variant="outline"
                  borderRadius="xl"
                  size="md"
                  colorScheme="blue"
                >
                  تحديث البيانات
                </Button>
                <Button
                  leftIcon={<DownloadIcon />}
                  onClick={handleExportExcel}
                  colorScheme="green"
                  borderRadius="xl"
                  size="md"
                  boxShadow="sm"
                >
                  تصدير إلى Excel
                </Button>
              </HStack>
            </Flex>
          </motion.div>

          {/* Séance Rule Definition Alert */}
          <Alert status="info" borderRadius="xl" borderWidth="1px" borderColor="blue.200" bg="blue.50">
            <AlertIcon as={InfoIcon} color="blue.500" boxSize={5} />
            <Box>
              <AlertTitle fontSize="sm" fontWeight="bold" color="blue.800">
                معيار احتساب الحصة المعتمدة (Séance):
              </AlertTitle>
              <AlertDescription fontSize="xs" color="blue.700" mt={0.5}>
                تُعتبر الحصة **حصة رسمية معتمدة** إذا بلغت مدتها الفعلية **1 ساعة و 20 دقيقة (80 دقيقة = 4,800 ثانية)** فأكثر. أي لقاء يقل عن هذا الحد يُسجل كلقاء قصير.
              </AlertDescription>
            </Box>
          </Alert>

          {/* Date Period Filter Bar */}
          <Card bg={cardBg} borderRadius="2xl" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
            <CardBody p={6}>
              <VStack spacing={4} align="stretch">
                <Text fontWeight="bold" color="gray.700" fontSize="sm">
                  <Icon as={FiCalendar} ml={2} color="blue.500" />
                  تحديد الفترة الزمنية للتقرير:
                </Text>

                <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                  {/* Preset Buttons */}
                  <ButtonGroup size="sm" isAttached variant="outline" borderRadius="lg">
                    <Button
                      colorScheme={preset === 'today' ? 'blue' : 'gray'}
                      variant={preset === 'today' ? 'solid' : 'outline'}
                      onClick={() => applyPreset('today')}
                    >
                      اليوم
                    </Button>
                    <Button
                      colorScheme={preset === 'thisWeek' ? 'blue' : 'gray'}
                      variant={preset === 'thisWeek' ? 'solid' : 'outline'}
                      onClick={() => applyPreset('thisWeek')}
                    >
                      هذا الأسبوع
                    </Button>
                    <Button
                      colorScheme={preset === 'thisMonth' ? 'blue' : 'gray'}
                      variant={preset === 'thisMonth' ? 'solid' : 'outline'}
                      onClick={() => applyPreset('thisMonth')}
                    >
                      هذا الشهر
                    </Button>
                    <Button
                      colorScheme={preset === 'lastMonth' ? 'blue' : 'gray'}
                      variant={preset === 'lastMonth' ? 'solid' : 'outline'}
                      onClick={() => applyPreset('lastMonth')}
                    >
                      الشهر الماضي
                    </Button>
                    <Button
                      colorScheme={preset === 'allTime' ? 'blue' : 'gray'}
                      variant={preset === 'allTime' ? 'solid' : 'outline'}
                      onClick={() => applyPreset('allTime')}
                    >
                      كل الأوقات
                    </Button>
                  </ButtonGroup>

                  {/* Custom Date Range Pickers */}
                  <HStack spacing={3} wrap="wrap">
                    <HStack spacing={2}>
                      <Text fontSize="xs" fontWeight="semibold" color="gray.600">من:</Text>
                      <Input
                        type="date"
                        size="sm"
                        borderRadius="lg"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          setPreset('custom');
                        }}
                        maxW="160px"
                        bg="gray.50"
                      />
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontSize="xs" fontWeight="semibold" color="gray.600">إلى:</Text>
                      <Input
                        type="date"
                        size="sm"
                        borderRadius="lg"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          setPreset('custom');
                        }}
                        maxW="160px"
                        bg="gray.50"
                      />
                    </HStack>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={fetchReport}
                      isLoading={loading}
                      borderRadius="lg"
                    >
                      تطبيق
                    </Button>
                  </HStack>
                </Flex>
              </VStack>
            </CardBody>
          </Card>

          {/* KPI Statistics Cards */}
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5}>
              {/* Total Séances */}
              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="2xl" boxShadow="sm" borderWidth="1px" borderColor={borderColor} borderTop="4px solid" borderTopColor="green.500">
                  <CardBody p={5}>
                    <Flex justify="space-between" align="center">
                      <Stat>
                        <StatLabel fontSize="xs" color="gray.500" fontWeight="bold">
                          إجمالي الحصص المعتمدة (≥ 80د)
                        </StatLabel>
                        <StatNumber fontSize="3xl" fontWeight="900" color="green.600" mt={1}>
                          {summary.totalSeances ?? 0}
                        </StatNumber>
                        <StatHelpText fontSize="xs" color="gray.500" mb={0}>
                          {summary.totalSeanceDurationFormatted ? `مدة الحصص: ${summary.totalSeanceDurationFormatted}` : 'حصة مكتملة'}
                        </StatHelpText>
                      </Stat>
                      <Box p={3.5} bg="green.50" color="green.500" borderRadius="2xl">
                        <Icon as={FiCheckCircle} boxSize={7} />
                      </Box>
                    </Flex>
                  </CardBody>
                </Card>
              </motion.div>

              {/* Total Teaching Hours */}
              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="2xl" boxShadow="sm" borderWidth="1px" borderColor={borderColor} borderTop="4px solid" borderTopColor="blue.500">
                  <CardBody p={5}>
                    <Flex justify="space-between" align="center">
                      <Stat>
                        <StatLabel fontSize="xs" color="gray.500" fontWeight="bold">
                          إجمالي مدة التدريس المباشر
                        </StatLabel>
                        <StatNumber fontSize="3xl" fontWeight="900" color="blue.600" mt={1}>
                          {summary.totalDurationFormatted || '0د'}
                        </StatNumber>
                        <StatHelpText fontSize="xs" color="gray.500" mb={0}>
                          عبر كل اللقاءات في الفترة
                        </StatHelpText>
                      </Stat>
                      <Box p={3.5} bg="blue.50" color="blue.500" borderRadius="2xl">
                        <Icon as={FiClock} boxSize={7} />
                      </Box>
                    </Flex>
                  </CardBody>
                </Card>
              </motion.div>

              {/* Active Instructors */}
              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="2xl" boxShadow="sm" borderWidth="1px" borderColor={borderColor} borderTop="4px solid" borderTopColor="purple.500">
                  <CardBody p={5}>
                    <Flex justify="space-between" align="center">
                      <Stat>
                        <StatLabel fontSize="xs" color="gray.500" fontWeight="bold">
                          الأساتذة النشطون بالفترة
                        </StatLabel>
                        <StatNumber fontSize="3xl" fontWeight="900" color="purple.600" mt={1}>
                          {summary.activeInstructors ?? 0}
                          <Text as="span" fontSize="sm" color="gray.400" fontWeight="normal" mr={1}>
                            / {summary.totalInstructors ?? 0}
                          </Text>
                        </StatNumber>
                        <StatHelpText fontSize="xs" color="gray.500" mb={0}>
                          أجروا حصة واحدة على الأقل
                        </StatHelpText>
                      </Stat>
                      <Box p={3.5} bg="purple.50" color="purple.500" borderRadius="2xl">
                        <Icon as={FiUsers} boxSize={7} />
                      </Box>
                    </Flex>
                  </CardBody>
                </Card>
              </motion.div>

              {/* Total Meetings (All) */}
              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="2xl" boxShadow="sm" borderWidth="1px" borderColor={borderColor} borderTop="4px solid" borderTopColor="orange.500">
                  <CardBody p={5}>
                    <Flex justify="space-between" align="center">
                      <Stat>
                        <StatLabel fontSize="xs" color="gray.500" fontWeight="bold">
                          إجمالي اللقاءات المباشرة
                        </StatLabel>
                        <StatNumber fontSize="3xl" fontWeight="900" color="orange.600" mt={1}>
                          {summary.totalMeetings ?? 0}
                        </StatNumber>
                        <StatHelpText fontSize="xs" color="orange.600" mb={0}>
                          {summary.totalShortMeetings ?? 0} لقاءات قصيرة (&lt; 80د)
                        </StatHelpText>
                      </Stat>
                      <Box p={3.5} bg="orange.50" color="orange.500" borderRadius="2xl">
                        <Icon as={FiActivity} boxSize={7} />
                      </Box>
                    </Flex>
                  </CardBody>
                </Card>
              </motion.div>
            </SimpleGrid>
          </motion.div>

          {/* Search, Filter and Table Section */}
          <Card bg={cardBg} borderRadius="2xl" boxShadow="sm" borderWidth="1px" borderColor={borderColor} overflow="hidden">
            <CardHeader p={6} borderBottomWidth="1px" borderColor={borderColor} bg="white">
              <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                {/* Search Bar */}
                <InputGroup maxW={{ base: '100%', md: '360px' }}>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="ابحث باسم الأستاذ، المادة، الهاتف..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    borderRadius="xl"
                    bg="gray.50"
                  />
                </InputGroup>

                {/* Filter and Sort Controls */}
                <HStack spacing={3} wrap="wrap">
                  <Select
                    size="md"
                    borderRadius="xl"
                    value={seanceFilter}
                    onChange={(e) => setSeanceFilter(e.target.value)}
                    maxW="180px"
                    bg="gray.50"
                  >
                    <option value="all">كل الأساتذة</option>
                    <option value="withSeances">لديهم حصص معتمدة (&gt; 0)</option>
                    <option value="noSeances">بدون حصص معتمدة (0)</option>
                  </Select>

                  <Select
                    size="md"
                    borderRadius="xl"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    maxW="200px"
                    bg="gray.50"
                  >
                    <option value="seances">ترتيب: الحصص المعتمدة</option>
                    <option value="meetings">ترتيب: إجمالي اللقاءات</option>
                    <option value="duration">ترتيب: مدة التدريس</option>
                    <option value="name">ترتيب: أبجدياً</option>
                  </Select>
                </HStack>
              </Flex>
            </CardHeader>

            {loading ? (
              <Box py={20} textAlign="center">
                <CobraSpinner size="xl" />
                <Text mt={4} color="gray.500" fontWeight="medium">
                  جاري جلب وتحليل حصص الأساتذة في الفترة المحددة...
                </Text>
              </Box>
            ) : filteredInstructors.length === 0 ? (
              <Box py={16} textAlign="center" color="gray.500">
                <Icon as={FiAlertCircle} boxSize={10} color="gray.300" mb={3} />
                <Text fontSize="lg" fontWeight="semibold">لم يتم العثور على أي نتائج</Text>
                <Text fontSize="sm" color="gray.400" mt={1}>
                  جرب تغيير خيارات البحث أو تحديد فترة زمنية أوسع.
                </Text>
              </Box>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th fontSize="xs">الأستاذ</Th>
                      <Th fontSize="xs">المواد الدراسية</Th>
                      <Th fontSize="xs">رقم الهاتف</Th>
                      <Th fontSize="xs" textAlign="center">
                        الحصص المعتمدة (≥ 1س 20د)
                      </Th>
                      <Th fontSize="xs" textAlign="center">لقاءات قصيرة (&lt; 1س 20د)</Th>
                      <Th fontSize="xs" textAlign="center">إجمالي اللقاءات</Th>
                      <Th fontSize="xs">إجمالي المدة</Th>
                      <Th fontSize="xs">معدل اللقاء</Th>
                      <Th fontSize="xs" textAlign="center">التفاصيل</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredInstructors.map((record) => {
                      const inst = record.instructor || {};
                      const seanceCount = record.seanceCount || 0;
                      const totalMeetings = record.totalMeetings || 0;
                      const shortCount = record.shortMeetingCount || 0;

                      return (
                        <Tr key={inst._id} _hover={{ bg: hoverRowBg }} transition="background 0.15s">
                          {/* Instructor Profile */}
                          <Td>
                            <HStack spacing={3}>
                              <Avatar
                                size="sm"
                                name={inst.username || 'أستاذ'}
                                bg={seanceCount > 0 ? 'blue.500' : 'gray.400'}
                                color="white"
                                fontWeight="bold"
                              />
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="bold" color="gray.800" fontSize="sm">
                                  {inst.username || 'غير معروف'}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {inst.email || '---'}
                                </Text>
                              </VStack>
                            </HStack>
                          </Td>

                          {/* Matieres */}
                          <Td maxW="200px">
                            <Flex wrap="wrap" gap={1}>
                              {inst.matiers && inst.matiers.length > 0 ? (
                                inst.matiers.map((mat, i) => (
                                  <Tag key={i} size="sm" colorScheme="blue" borderRadius="full" fontSize="2xs">
                                    {mat}
                                  </Tag>
                                ))
                              ) : (
                                <Text fontSize="xs" color="gray.400">---</Text>
                              )}
                            </Flex>
                          </Td>

                          {/* Phone */}
                          <Td dir="ltr" textAlign="right" fontSize="sm" color="gray.600">
                            {inst.phoneNumber || '---'}
                          </Td>

                          {/* Valid Séances (Highlight) */}
                          <Td textAlign="center">
                            <Tag
                              size="lg"
                              colorScheme={seanceCount > 0 ? 'green' : 'gray'}
                              borderRadius="xl"
                              px={3.5}
                              py={1.5}
                              fontWeight="black"
                              fontSize="md"
                              boxShadow={seanceCount > 0 ? 'xs' : 'none'}
                            >
                              {seanceCount > 0 && <CheckCircleIcon mr={1.5} boxSize={3.5} />}
                              {seanceCount} {seanceCount === 1 ? 'حصة' : 'حصص'}
                            </Tag>
                          </Td>

                          {/* Short Meetings */}
                          <Td textAlign="center">
                            <Tag
                              size="md"
                              colorScheme={shortCount > 0 ? 'orange' : 'gray'}
                              borderRadius="lg"
                              px={2.5}
                              py={1}
                              fontSize="xs"
                              fontWeight="bold"
                            >
                              {shortCount}
                            </Tag>
                          </Td>

                          {/* Total Meetings */}
                          <Td textAlign="center" fontWeight="bold" color="gray.700">
                            {totalMeetings}
                          </Td>

                          {/* Total Duration */}
                          <Td fontWeight="bold" color="blue.600" fontSize="sm">
                            {record.totalDurationFormatted || '0د'}
                          </Td>

                          {/* Average Duration */}
                          <Td fontSize="xs" color="gray.600">
                            {record.averageDurationFormatted || '0د'}
                          </Td>

                          {/* Action Button */}
                          <Td textAlign="center">
                            <Button
                              size="sm"
                              colorScheme="blue"
                              variant="outline"
                              leftIcon={<ViewIcon />}
                              borderRadius="lg"
                              onClick={() => handleOpenDetails(record)}
                              isDisabled={totalMeetings === 0}
                            >
                              عرض الحصص ({totalMeetings})
                            </Button>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Card>
        </VStack>

        {/* Instructor Drill-Down Modal */}
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          size="4xl"
          isCentered
          motionPreset="slideInBottom"
          scrollBehavior="inside"
        >
          <ModalOverlay backdropFilter="blur(5px)" />
          <ModalContent dir="rtl" borderRadius="2xl" overflow="hidden" boxShadow="2xl">
            <ModalHeader bg="blue.600" color="white" py={4}>
              <HStack justify="space-between" align="center" wrap="wrap" gap={2}>
                <HStack spacing={3}>
                  <Avatar
                    size="sm"
                    name={selectedInstructor?.instructor?.username || 'أستاذ'}
                    bg="white"
                    color="blue.600"
                    fontWeight="bold"
                  />
                  <Box>
                    <Text fontSize="md" fontWeight="bold">
                      تفاصيل حصص الأستاذ: {selectedInstructor?.instructor?.username}
                    </Text>
                    <Text fontSize="xs" color="blue.100" fontWeight="normal">
                      {selectedInstructor?.instructor?.email} • {selectedInstructor?.instructor?.phoneNumber || 'بدون هاتف'}
                    </Text>
                  </Box>
                </HStack>
                <Badge colorScheme="green" fontSize="sm" px={3} py={1} borderRadius="full" variant="solid">
                  {selectedInstructor?.seanceCount || 0} حصة معتمدة (≥ 1س 20د)
                </Badge>
              </HStack>
            </ModalHeader>
            <ModalCloseButton color="white" left="4" right="auto" top="4" />

            <ModalBody p={6} bg="gray.50">
              {selectedInstructor && (
                <VStack spacing={5} align="stretch">
                  {/* Summary Banner inside modal */}
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                    <Box bg="white" p={3.5} borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" textAlign="center">
                      <Text fontSize="xs" color="gray.500">الحصص المعتمدة (≥ 1س 20د)</Text>
                      <Text fontSize="xl" fontWeight="black" color="green.600" mt={1}>
                        {selectedInstructor.seanceCount}
                      </Text>
                    </Box>
                    <Box bg="white" p={3.5} borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" textAlign="center">
                      <Text fontSize="xs" color="gray.500">اللقاءات القصيرة (&lt; 1س 20د)</Text>
                      <Text fontSize="xl" fontWeight="black" color="orange.500" mt={1}>
                        {selectedInstructor.shortMeetingCount}
                      </Text>
                    </Box>
                    <Box bg="white" p={3.5} borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" textAlign="center">
                      <Text fontSize="xs" color="gray.500">إجمالي وقت التدريس</Text>
                      <Text fontSize="xl" fontWeight="black" color="blue.600" mt={1}>
                        {selectedInstructor.totalDurationFormatted}
                      </Text>
                    </Box>
                    <Box bg="white" p={3.5} borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" textAlign="center">
                      <Text fontSize="xs" color="gray.500">إجمالي اللقاءات</Text>
                      <Text fontSize="xl" fontWeight="black" color="gray.700" mt={1}>
                        {selectedInstructor.totalMeetings}
                      </Text>
                    </Box>
                  </SimpleGrid>

                  {/* List of Meetings */}
                  <Box bg="white" borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" overflow="hidden">
                    <Box p={4} borderBottomWidth="1px" borderColor="gray.100" bg="gray.50">
                      <Text fontWeight="bold" fontSize="sm" color="gray.700">
                        سجل الحصص واللقاءات في الفترة المحددة ({selectedInstructor.meetings?.length || 0})
                      </Text>
                    </Box>

                    {(!selectedInstructor.meetings || selectedInstructor.meetings.length === 0) ? (
                      <Box p={8} textAlign="center" color="gray.400">
                        لا توجد حصص مسجلة في الفترة المختارة.
                      </Box>
                    ) : (
                      <Box overflowX="auto" maxH="400px" overflowY="auto">
                        <Table size="sm" variant="simple">
                          <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                            <Tr>
                              <Th>عنوان الغرفة / المادة</Th>
                              <Th>رقم الحصة</Th>
                              <Th>تاريخ وتوقيت البدء</Th>
                              <Th>المدة الفعلية</Th>
                              <Th>الحالة والاعتماد</Th>
                              <Th textAlign="center">الطلاب الحاضرين</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {selectedInstructor.meetings.map((m) => {
                              const isSeance = m.isSeance;
                              const startDateObj = m.createdAt ? new Date(m.createdAt) : null;
                              const endDateObj = m.endedAt ? new Date(m.endedAt) : null;

                              return (
                                <Tr key={m._id} bg={isSeance ? 'green.50' : 'transparent'}>
                                  <Td fontWeight="bold" color="gray.800">
                                    {m.roomName || 'بدون اسم'}
                                    {m.status === 'active' && (
                                      <Badge colorScheme="blue" ml={2} fontSize="2xs">
                                        مباشر الآن
                                      </Badge>
                                    )}
                                  </Td>

                                  <Td dir="ltr" textAlign="right" fontFamily="mono" color="gray.600">
                                    {m.meetingId || '---'}
                                  </Td>

                                  <Td>
                                    {startDateObj ? (
                                      <VStack align="start" spacing={0}>
                                        <Text fontSize="xs" fontWeight="semibold">
                                          {startDateObj.toLocaleDateString('ar-EG', { numberingSystem: 'latn' })}
                                        </Text>
                                        <Text fontSize="2xs" color="gray.500">
                                          من {startDateObj.toLocaleTimeString('ar-EG', { numberingSystem: 'latn', hour: '2-digit', minute: '2-digit' })}
                                          {endDateObj && ` إلى ${endDateObj.toLocaleTimeString('ar-EG', { numberingSystem: 'latn', hour: '2-digit', minute: '2-digit' })}`}
                                        </Text>
                                      </VStack>
                                    ) : (
                                      '---'
                                    )}
                                  </Td>

                                  <Td>
                                    <Tag
                                      size="sm"
                                      colorScheme={isSeance ? 'green' : 'orange'}
                                      fontWeight="bold"
                                      borderRadius="md"
                                    >
                                      <TimeIcon mr={1} />
                                      {m.durationFormatted || '0د'}
                                    </Tag>
                                  </Td>

                                  <Td>
                                    {isSeance ? (
                                      <Badge colorScheme="green" px={2.5} py={1} borderRadius="md" display="inline-flex" alignItems="center">
                                        <CheckCircleIcon mr={1} /> حصة معتمدة (≥ 1س 20د)
                                      </Badge>
                                    ) : (
                                      <Tooltip label={`أقل بـ ${m.shortfallFormatted || ''} عن المعيار الأدنى (1س 20د)`} hasArrow>
                                        <Badge colorScheme="orange" px={2.5} py={1} borderRadius="md" display="inline-flex" alignItems="center">
                                          <WarningIcon mr={1} /> لقاء قصير (&lt; 1س 20د)
                                        </Badge>
                                      </Tooltip>
                                    )}
                                  </Td>

                                  <Td textAlign="center">
                                    <Badge colorScheme="purple" borderRadius="full" px={2} py={0.5}>
                                      {m.studentCount || 0} طالب
                                    </Badge>
                                  </Td>
                                </Tr>
                              );
                            })}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </Box>
                </VStack>
              )}
            </ModalBody>

            <ModalFooter bg="white" borderTopWidth="1px" borderColor="gray.100" py={3}>
              <Button onClick={onClose} colorScheme="blue" borderRadius="lg" px={6}>
                إغلاق
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

      </Box>
    </ResponsiveLayout>
  );
}
